import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import LoadingIndicator from 'src/components/common/LoadingIndicator'
import { eventFormSelector, flyerSelector } from 'src/selectors'
import cn from 'classnames'
import { Design } from 'src/models/design.model'
import styles from './video-flyer-card.scss'
import SvgIcon from 'components/common/SvgIcon'
import CheckCircleIcon from 'src/assets/icons/check-circle.svg'
import FlyerTextElement from '../FlyerCard/FlyerTextElement/FlyerTextElement'
import { changeEventForm } from 'src/actions/event'
import FlyerFonts from 'assets/frontend-json/flyer-fonts.json'

const THUMB_WIDTH = 320

const VideoFlyerCard = forwardRef(({
  isThumbnail = false,
  flyerDetail,
  containerClassName,
  showCheckIcon = true
}, ref) => {
  const dispatch = useDispatch()
  const eventForm = useSelector(eventFormSelector)
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const videoContainerRef = useRef(null)

  const [isLoadingFlyer, setIsLoadingFlyer] = useState(true)
  const [isLoadedMedia, setIsLoadedMedia] = useState(false)
  const { customFlyerURL } = useSelector(flyerSelector)

  const { layout } = new Design(flyerDetail, eventForm)
  const flyerTexts = layout?.getTexts() || []
  const layoutImages = layout?.getFlourishes() || []

  const [backGroundImageInstance, setBackGroundImageInstance] = useState(null)
  const overlayImageRefs = useRef([])

  const isFlyerSelected = eventForm.flyer_design_number === flyerDetail.design_number

  useEffect(() => {
    if (customFlyerURL) {
      setIsLoadingFlyer(true)
      const IMG = new window.Image()
      IMG.crossOrigin = 'Anonymous'
      IMG.onload = () => {
        setBackGroundImageInstance(IMG)
        setIsLoadedMedia(true)
        setIsLoadingFlyer(false)
      }
      IMG.onerror = () => {
        setIsLoadingFlyer(false)
      }
      IMG.src = customFlyerURL
    }
  }, [customFlyerURL])

  useEffect(() => {
    dispatch(changeEventForm({ mediaType: 'video' }))
  }, [])

  useEffect(() => {
    onLoadFonts()
  }, [])

  const onLoadFonts = async () => {
    const fonts = FlyerFonts.fonts
    const fontPromises = fonts.map(font => {
      const fontName = font.replace(/\.(ttf|otf|woff2|ttc)$/i, '')
      const fontFace = new FontFace(fontName, `url(/assets/Fonts/${font})`)
      return fontFace.load().then(loadedFace => {
        document.fonts.add(loadedFace)
      })
    })
    await Promise.all(fontPromises)
  }

  const handleVideoLoaded = () => {
    setIsLoadedMedia(true)
    setIsLoadingFlyer(false)
  }

  const isMountedRef = useRef(true)
  const animationIdRef = useRef(null)
  const timeoutIdRef = useRef(null)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current)
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    generateImage: generateCompositeImage
  }))

  const handleClick = () => {
    if (isThumbnail) return
    const { design_number, design_slug } = flyerDetail
    dispatch(
      changeEventForm({
        flyer_design_number: isFlyerSelected ? null : design_number,
        flyer_design_slug: isFlyerSelected ? null : design_slug,
        checkedFlyer: isFlyerSelected ? null : eventForm.checkedFlyer,
        mediaType: isFlyerSelected ? null : 'video'
      })
    )
  }

  const renderTextLayer = () => {
    if (!isLoadedMedia || !videoContainerRef.current || flyerTexts.length === 0) return null
    const ratio = videoContainerRef.current.clientWidth / THUMB_WIDTH
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 4 }}>
        <FlyerTextElement
          flyerTexts={flyerTexts}
          ratio={ratio}
        />
      </div>
    )
  }

  const renderOverlayImage = () => {
    if (!videoContainerRef.current) return null
    const ratio = videoContainerRef.current.clientWidth / THUMB_WIDTH
    if (!layoutImages || layoutImages.length === 0) return null

    return layoutImages.map((item, index) => {
      let srcProp
      try {
        srcProp = require(`src/assets/Layouts/Flourishes/${item.name}.png`)
      } catch (e) {
        return null
      }
      const imgStyle = {
        position: 'absolute',
        left: item.left * ratio,
        top: item.top * ratio,
        width: item.width * ratio,
        height: item.height * ratio,
        pointerEvents: 'none',
        zIndex: 3
      }
      return (
        <img
          key={index}
          src={srcProp}
          style={imgStyle}
          data-item={JSON.stringify(item)}
          crossOrigin="Anonymous"
          alt={`${item.name} can't see images? try to refresh the page.`}
          ref={ref => (overlayImageRefs.current[index] = ref)}
        />
      )
    })
  }

  const writeWrapText = (context, item, canvasWidth, canvasHeight, ratio) => {
    let {
      text,
      size,
      align,
      right,
      left,
      top,
      width,
      height,
      font,
      widthAlign,
      nowrap,
      verticalAlign,
      rowGap = 0,
      lineHeight: lineHeightProp,
      textParts
    } = item
    let x = align === 'right' ? right * ratio : left * ratio
    let y = top * ratio
    let maxWidth = width ? width * ratio : canvasWidth
    let maxHeight = height ? height * ratio : canvasHeight

    let lineHeight = lineHeightProp ? lineHeightProp * ratio + rowGap : size * ratio + rowGap

    if (Array.isArray(textParts) && textParts.length > 0) {
      drawTextParts(context, textParts, {
        x,
        y,
        maxWidth,
        align,
        size,
        font,
        lineHeight,
        ratio,
        color: item.color?.replace('0x', '#') || '#000'
      })
      return
    }

    let isExceedHeight = true

    const textBlocks = text ? text.split('\n') : ['']
    const MIN_FONT_SIZE = 5
    while (isExceedHeight) {
      context.font = `${size * ratio || 16}px ${font}`
      let lines = []
      let currentY = y

      for (let blockIdx = 0; blockIdx < textBlocks.length; blockIdx++) {
        let block = textBlocks[blockIdx]
        let words = nowrap ? [block] : block.split(' ')
        let line = ''
        for (let n = 0; n < words.length; n++) {
          let testLine = line + words[n] + ' '
          let testWidth = context.measureText(testLine).width
          if (testWidth > maxWidth && n > 0) {
            lines.push({
              text: line,
              x: writeTextAlign(context, x, line, align, maxWidth, widthAlign, canvasWidth),
              y: currentY
            })
            line = words[n] + ' '
            currentY += lineHeight
          } else {
            line = testLine
          }
        }
        lines.push({
          text: line,
          x: writeTextAlign(context, x, line, align, maxWidth, widthAlign, canvasWidth),
          y: currentY
        })
        currentY += lineHeight
      }

      if (lineHeight * lines.length - rowGap > maxHeight) {
        size -= 0.3
        if (size < MIN_FONT_SIZE) {
          size = MIN_FONT_SIZE
          context.font = `${size * ratio}px ${font}`
          lineHeight = lineHeightProp ? lineHeightProp * ratio + rowGap : size * ratio + rowGap

          const maxLines = Math.floor(maxHeight / lineHeight)
          if (lines.length > maxLines) {
            lines = lines.slice(0, maxLines)
            const lastLine = lines[lines.length - 1]
            lastLine.text = lastLine.text.trimEnd() + ' ...'
          }
          writeCanvasText({ context, lines, size: size * ratio, item })
          break
        }
        lineHeight = lineHeightProp ? lineHeightProp * ratio + rowGap : size * ratio + rowGap
        currentY = y
      } else {
        let isExceedWidth = lines.some(i => context.measureText(i.text).width > maxWidth)
        if (!isExceedWidth) {
          isExceedHeight = false
        if (verticalAlign) {
            lines = handleVerticalAlign({
              lines,
              maxHeight,
              lineHeight,
              rowGap,
              verticalAlign
            })
          }
          writeCanvasText({ context, lines, size: size * ratio, item })
        } else {
          size -= 0.3
          if (size < MIN_FONT_SIZE) {
            size = MIN_FONT_SIZE
            context.font = `${size * ratio}px ${font}`
            lineHeight = lineHeightProp ? lineHeightProp * ratio + rowGap : size * ratio + rowGap

            const maxLines = Math.floor(maxHeight / lineHeight)
            if (lines.length > maxLines) {
              lines = lines.slice(0, maxLines)
              const lastLine = lines[lines.length - 1]
              lastLine.text = lastLine.text.trimEnd() + ' ...'
            }
            writeCanvasText({ context, lines, size: size * ratio, item })
            break
          }
          lineHeight = lineHeightProp ? lineHeightProp * ratio + rowGap : size * ratio + rowGap
          currentY = y
        }
      }
    }
  }

  const handleVerticalAlign = params => {
    const { lines, maxHeight, lineHeight, rowGap, verticalAlign } = params
    const linesHeight = lineHeight * lines.length - rowGap
    const gapHeight = (maxHeight - linesHeight) / (verticalAlign === 'center' ? 2 : 1)
    return lines.map(item => ({
      text: item.text,
      x: item.x,
      y: item.y + gapHeight
    }))
  }

  const writeCanvasText = params => {
    const { context, lines, size, item } = params
    context.beginPath()
    context.font = `${size || 16}px ${item.font}`
    context.fillStyle = item.color.replace('0x', '#')
    context.textBaseline = 'top'
    if (item.align === 'center') {
      context.textAlign = item.align
    } else {
      context.textAlign = 'left'
    }
    for (let line of lines) {
      const { text, x, y } = line
      context.fillText(text, x, y)
    }
    context.closePath()
  }

  const writeTextAlign = (ctx, x, text, align, maxWidth, widthAlign, canvasWidth) => {
    if (align === 'center') {
      return canvasWidth / 2
    } else if (align === 'right') {
      if (widthAlign === 'center') {
        return (
          canvasWidth -
          ctx.measureText(text).width -
          (maxWidth - ctx.measureText(text).width) / 2 -
          x
        )
      }
      return canvasWidth - ctx.measureText(text).width - x
    } else {
      if (widthAlign === 'center') {
        return (maxWidth - ctx.measureText(text).width) / 2 + x
      }
      return x
    }
  }

  function drawTextParts(context, textParts, {
    x, y, maxWidth, align = 'left', size = 16, font = 'Arial', lineHeight = 20, ratio = 1, color = '#000'
  }) {
    const lines = []
    let currentLine = []
    textParts.forEach(part => {
      if (part.text.startsWith('\n')) {
        if (currentLine.length) lines.push(currentLine)
        currentLine = [{ ...part, text: part.text.replace(/^\n/, '') }]
      } else {
        currentLine.push(part)
      }
    })
    if (currentLine.length) lines.push(currentLine)

    let currentY = y

    for (const lineParts of lines) {
      let lineWidth = 0
      lineParts.forEach(part => {
        context.font = `${(part.size || size) * ratio}px ${part.font || font}`
        lineWidth += context.measureText(part.text).width
      })

      let baseX
      if (align === 'center') {
        baseX = x + (maxWidth - lineWidth) / 2
      } else if (align === 'right') {
        baseX = x + maxWidth - lineWidth
      } else {
        baseX = x
      }
      let partX = baseX
      for (const part of lineParts) {
        context.font = `${(part.size || size) * ratio}px ${part.font || font}`
        context.fillStyle = part.color || color
        context.textBaseline = 'top'
        context.textAlign = 'left'
        const fontAdjust = part.font === 'Satisfy-Regular' ? 5 : 0
        context.fillText(part.text, partX, currentY + fontAdjust)
        partX += context.measureText(part.text).width
      }
      currentY += lineHeight
    }
  }

  const generateCompositeImage = async () => {
    if (customFlyerURL) {
      if (!backGroundImageInstance || !backGroundImageInstance.complete) return
    } else {
      if (!isLoadedMedia || !videoRef.current) return
    }
    if (!isMountedRef.current) return

    const width = customFlyerURL
      ? backGroundImageInstance.width
      : videoRef.current.videoWidth
    const height = customFlyerURL
      ? backGroundImageInstance.height
      : videoRef.current.videoHeight

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = width
    canvas.height = height

    const ratio = width / THUMB_WIDTH

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (customFlyerURL && backGroundImageInstance && backGroundImageInstance.complete) {
      ctx.drawImage(backGroundImageInstance, 0, 0, canvas.width, canvas.height)
    }

    const layoutImageEls = overlayImageRefs.current
    if (layoutImageEls.length > 0) {
      for (let i = 0; i < layoutImageEls.length; i++) {
        const imageEl = layoutImageEls[i]
        if (!imageEl) continue
        const imagePosition = JSON.parse(imageEl.getAttribute('data-item'))
        const layoutWidth = imagePosition.width * ratio
        const layoutHeight = imagePosition.height * ratio
        const layoutTop = imagePosition.top * ratio
        const layoutLeft = imagePosition.left * ratio
        ctx.drawImage(imageEl, layoutLeft, layoutTop, layoutWidth, layoutHeight)
      }
    }

    flyerTexts.forEach((item) => {
      if (item.type === 'date' && item.dates) {
        item.dates.forEach(date => {
          writeWrapText(ctx, date, canvas.width, canvas.height, ratio)
        })
      } else {
        writeWrapText(ctx, item, canvas.width, canvas.height, ratio)
      }
    })

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Canvas is empty'))
        }
      })
    })
  }

  return (
    <div ref={containerRef} className={cn(styles['container'], containerClassName)}>
      <div
        className={cn(styles['flyer-container'], {
          [styles['flyer-container-checked']]: isFlyerSelected
        })}
        onClick={handleClick}
      >
        {showCheckIcon && isFlyerSelected && (
          <SvgIcon className={styles['check-icon']} icon={CheckCircleIcon} />
        )}

        <div
          ref={videoContainerRef}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%'
          }}
        >
          {customFlyerURL ? (
            <img
              src={customFlyerURL}
              alt="custom flyer"
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 2,
                objectFit: 'cover'
              }}
              onLoad={handleVideoLoaded}
              onError={() => {
                console.error('Custom flyer image load error')
                setIsLoadingFlyer(false)
              }}
            />
          ) : (
            <video
              ref={videoRef}
              src={`${process.env.AWS_S3_CDN_URL}/video_flyers/${flyerDetail.video_bg_slug}.mp4`}
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
                objectFit: 'cover'
              }}
              autoPlay
              loop
              muted
              playsInline
              crossOrigin="anonymous"
              onLoadedData={handleVideoLoaded}
              onError={(event) => {
                const error = event.target.error
                let message = 'Video load error: '
                if (error) {
                  switch (error.code) {
                    case error.MEDIA_ERR_ABORTED:
                      message += 'Fetching process aborted by user.'
                      break
                    case error.MEDIA_ERR_NETWORK:
                      message += 'Network error occurred while fetching media.'
                      break
                    case error.MEDIA_ERR_DECODE:
                      message += 'Decoding error or unsupported format.'
                      break
                    case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                      message += 'Media source not supported.'
                      break
                    default:
                      message += 'An unknown error occurred.'
                  }
                } else {
                  message += 'Unknown error, no error object.'
                }
                console.error(message, error)
                setIsLoadingFlyer(false)
              }}
            />
          )}

          {isLoadedMedia && (
            <>
              {renderOverlayImage()}
              {renderTextLayer()}
            </>
          )}
        </div>

        {isLoadingFlyer && (
          <div className={styles['loading-container']}>
            <LoadingIndicator />
          </div>
        )}

      </div>
    </div>
  )
})

VideoFlyerCard.propTypes = {
  isThumbnail: PropTypes.bool,
  flyerDetail: PropTypes.object,
  containerClassName: PropTypes.string,
  showCheckIcon: PropTypes.bool
}

export default VideoFlyerCard
